import { useEffect, useMemo, useRef, useState } from "react";
import { useFormikContext } from "formik";
import { array, lazy, mixed, object, string, InferType } from "yup";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Badge,
  Box,
  Button,
  Callout,
  Card,
  Code,
  Flex,
  IconButton,
  Select,
  Tabs,
  Text,
  TextField,
} from "@radix-ui/themes";
import * as Dialog from "@radix-ui/react-dialog";
import HighZIndexDialog from "@/components/ui/HighZIndexDialog";
import {
  Check,
  Code2,
  Edit3,
  Eye,
  Layers,
  Lock,
  Maximize2,
  Minimize2,
  Paperclip,
  Plus,
  Save,
  Users,
  X,
} from "lucide-react";
import CodeMirror from "@uiw/react-codemirror";
import { html as cmHtml } from "@codemirror/lang-html";
import { EditorView } from "@codemirror/view";
import { formatHtml } from "@/utils/htmlFormatter";
import type { Editor as TipTapEditor } from "@tiptap/react";
import CodeField from "@/components/form/CodeField";
import { RichTextEditor } from "@/components/form/RichTextEditor";
import { emailTemplateService } from "@/services/breeze/EmailTemplateService";
import { EmailTemplate } from "@/types/breeze/EmailTemplate";
import { useThrottledPreview } from "@/hooks/useThrottledPreview";
import { useCaretInsert } from "@/hooks/useCaretInsert";
import { useTenantPlan } from "@/hooks/useCommonQueries";
import EmailAttachmentsField from "./shared/EmailAttachmentsField";
import VariableInputField from "./shared/VariableInputField";
import FieldSectionHeader from "./shared/FieldSectionHeader";
import VariableMenu from "./shared/VariableMenu";
import PayloadHelpDialog from "./shared/PayloadHelpDialog";
import EmailTemplateToolbar, { SubjectVariableButton } from "@/pages/breeze/settings/EmailTemplateToolbar";
import { ANYTHINK_TYPE_DEFINITIONS } from "./anythink-types";
import { WorkflowStep } from "@/types/breeze/Workflow";
import { detectCustomTokens, exampleFor } from "@/utils/emailTokens";
import { tenantDomainService } from "@/services/breeze/TenantDomainService";
import { composeFromAddress, splitFromAddress } from "@/utils/sendingDomain";

export const sendAnEmailValidationSchema = object({
  parameters: object().shape({
    to: string().required("To email address is required"),
    from: string().nullable().notRequired(),
    template_type: string().required("Email template is required"),
    payload: lazy((value) =>
      typeof value === "string" || value == null
        ? string().nullable().notRequired()
        : object().nullable().notRequired(),
    ),
    attachments: array()
      .transform((arr) =>
        Array.isArray(arr)
          ? arr.filter((a) => a && a.file_id !== undefined && a.file_id !== null && a.file_id !== "")
          : arr,
      )
      .of(
        object({
          file_id: mixed().nullable().notRequired(),
          disposition: string().oneOf(["attachment", "inline"]).default("attachment"),
          content_id: string().optional(),
        }),
      )
      .max(10, "Maximum of 10 attachments")
      .nullable()
      .notRequired()
      .default([]),
  }),
});

export type SendAnEmailFormValues = InferType<typeof sendAnEmailValidationSchema>;


interface SendAnEmailFieldsProps {
  existingSteps?: WorkflowStep[];
  triggerEntity?: string;
}

// Registry of pre-save hooks. Each mounted SendAnEmailFields registers via
// useEffect and unregisters on cleanup so concurrent instances (pop-out + inline,
// multiple drawers, StrictMode double-mount) can't clobber each other.
const templatePreSaveHooks = new Set<() => Promise<void>>();
export async function runEmailTemplatePreSave(): Promise<void> {
  for (const fn of Array.from(templatePreSaveHooks)) {
    await fn();
  }
}

function PopOutSaveBar({ onClose }: { onClose: () => void }) {
  const formik = useFormikContext<any>();
  const submitting = formik.isSubmitting;
  return (
    <Flex align="center" justify="between" px="4" py="3"
      style={{ borderBottom: "1px solid var(--gray-5)", flexShrink: 0 }}>
      <Text size="3" weight="medium">Edit step</Text>
      <Flex gap="2">
        <Button type="button" variant="soft" color="gray" size="2" onClick={onClose}>
          Close
        </Button>
        <Button type="button" size="2" disabled={submitting}
          onClick={async () => { await formik.submitForm(); }}>
          <Save size={14} /> {submitting ? "Saving…" : "Save step"}
        </Button>
      </Flex>
    </Flex>
  );
}

type TabKey = "edit" | "source" | "preview" | "mappings" | "attachments";

export default function SendAnEmailFields({ existingSteps = [], triggerEntity }: SendAnEmailFieldsProps) {
  const { values, setFieldValue } = useFormikContext<any>();
  const queryClient = useQueryClient();
  const subjectCaret = useCaretInsert();
  const { data: plan } = useTenantPlan();
  const isFreePlan = plan?.is_free === true;
  const isFreePlanRef = useRef(isFreePlan);
  isFreePlanRef.current = isFreePlan;

  const templateType = values?.parameters?.template_type as string | undefined;
  const payloadJson = (values?.parameters?.payload as string | undefined) ?? "{}";

  const { data: templates } = useQuery({
    queryKey: ["email-templates"],
    queryFn: () => emailTemplateService.getAllTemplates(),
  });

  const selectedTemplate = useMemo(
    () => templates?.find((t) => t.template_type === templateType),
    [templates, templateType],
  );

  const [editData, setEditData] = useState({ subject: "", content: "" });
  const [hasChanges, setHasChanges] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>("edit");
  const [popOut, setPopOut] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [sampleData, setSampleData] = useState<Record<string, string>>({});
  const editorViewRef = useRef<EditorView | null>(null);
  const tipTapEditorRef = useRef<TipTapEditor | null>(null);

  useEffect(() => {
    if (selectedTemplate) {
      setEditData({ subject: selectedTemplate.subject, content: selectedTemplate.content });
      setHasChanges(false);
      setSavedAt(null);
    }
  }, [selectedTemplate?.id]);

  useEffect(() => {
    if (!savedAt) return;
    const t = setTimeout(() => setSavedAt(null), 2000);
    return () => clearTimeout(t);
  }, [savedAt]);

  useEffect(() => {
    if (!popOut) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPopOut(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [popOut]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!selectedTemplate) throw new Error("No template selected");
      return emailTemplateService.updateTemplate(selectedTemplate.template_type, editData);
    },
    onSuccess: (saved) => {
      queryClient.setQueryData<EmailTemplate[] | undefined>(
        ["email-templates"],
        (prev) => prev?.map((t) => (t.template_type === saved.template_type ? saved : t)),
      );
      setHasChanges(false);
      setSavedAt(performance.now());
    },
  });

  const hasChangesRef = useRef(hasChanges);
  const selectedTemplateRef = useRef(selectedTemplate);
  const updateMutationRef = useRef(updateMutation);
  hasChangesRef.current = hasChanges;
  selectedTemplateRef.current = selectedTemplate;
  updateMutationRef.current = updateMutation;

  useEffect(() => {
    const hook = async () => {
      if (hasChangesRef.current && selectedTemplateRef.current && !isFreePlanRef.current) {
        await updateMutationRef.current.mutateAsync();
      }
    };
    templatePreSaveHooks.add(hook);
    return () => { templatePreSaveHooks.delete(hook); };
  }, []);

  const handleFieldChange = (key: keyof typeof editData, value: string) => {
    setEditData((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
    if (savedAt) setSavedAt(null);
  };

  const insertIntoActive = (text: string) => {
    const isImage = /^<img\b/i.test(text.trim());
    if (isImage && activeTab !== "source") {
      setActiveTab("source");
      handleFieldChange("content", formatHtml(appendWithSpacing(editData.content, text)));
      return;
    }
    if (activeTab === "source") {
      const view = editorViewRef.current;
      if (view && view.dom?.isConnected) {
        const pos = view.state.selection.main.head;
        view.dispatch({
          changes: { from: pos, insert: text },
          selection: { anchor: pos + text.length },
        });
        view.focus();
        return;
      }
      editorViewRef.current = null;
      handleFieldChange("content", appendWithSpacing(editData.content, text));
      return;
    }
    if (activeTab === "edit") {
      const ed = tipTapEditorRef.current;
      if (ed && !ed.isDestroyed) {
        ed.chain().focus().insertContent(text).run();
        return;
      }
      tipTapEditorRef.current = null;
    }
    handleFieldChange("content", appendWithSpacing(editData.content, text));
  };

  const previewArgs = useMemo(() => {
    if (!selectedTemplate) return null;
    return {
      subject: editData.subject,
      content: editData.content,
      variables: buildPreviewVariables(payloadJson, sampleData),
    };
  }, [selectedTemplate, editData.subject, editData.content, payloadJson, sampleData]);

  const previewHtml = useThrottledPreview(
    previewArgs ? JSON.stringify(previewArgs) : "",
    () => emailTemplateService.previewRaw(previewArgs!),
    !!previewArgs && activeTab === "preview",
    "",
  );

  const detectedTokens = useMemo(() => {
    if (!selectedTemplate) return [] as string[];
    return detectCustomTokens((editData.subject ?? "") + " " + (editData.content ?? ""));
  }, [editData.subject, editData.content, selectedTemplate]);

  const workflowContext = {
    existingSteps,
    triggerEntity,
    payloadJson,
    onPayloadChange: (next: string) => setFieldValue("parameters.payload", next),
  };

  const editorHeight = popOut ? "60vh" : "320px";
  const previewHeight = popOut ? "65vh" : "360px";

  const editorPanel = !selectedTemplate ? (
    <Card style={{ contain: "none" }}>
      <Box p="3"><Text color="gray">Pick a template above to start editing.</Text></Box>
    </Card>
  ) : (
    <Card style={{ contain: "none" }}>
      <Box p="3">
        <Box mb="3">
          <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>
            Subject
          </Text>
          <Flex gap="2" align="center">
            <TextField.Root
              ref={subjectCaret.inputRef}
              size="2"
              value={editData.subject}
              onChange={(e) => handleFieldChange("subject", e.target.value)}
              onBlur={subjectCaret.onBlur}
              placeholder="Email subject…"
              style={{ flex: 1 }}
            />
            <SubjectVariableButton
              content={`${editData.subject} ${editData.content}`}
              onInsert={(text) =>
                handleFieldChange("subject", subjectCaret.insertAtCaret(editData.subject, text))
              }
              workflowContext={workflowContext}
            />
          </Flex>
        </Box>

        <Tabs.Root
          value={activeTab}
          onValueChange={(v) => {
            const next = v as TabKey;
            // Pretty-print on entry so the Source view starts with one tag per
            // line rather than a single wrapped wall of HTML. We only rewrite
            // if the formatted output differs, to avoid loops, and we set the
            // active tab last so CodeMirror picks up the new value cleanly.
            if (next === "source" && editData.content) {
              const formatted = formatHtml(editData.content);
              if (formatted !== editData.content) {
                setEditData((prev) => ({ ...prev, content: formatted }));
              }
            }
            setActiveTab(next);
          }}
        >
          <Tabs.List>
            <Tabs.Trigger value="edit"><Edit3 size={14} /> Edit</Tabs.Trigger>
            <Tabs.Trigger value="source"><Code2 size={14} /> Source</Tabs.Trigger>
            <Tabs.Trigger value="preview"><Eye size={14} /> Preview</Tabs.Trigger>
            <Tabs.Trigger value="mappings">
              <Layers size={14} /> Mappings
              {detectedTokens.length > 0 && (
                <Badge color="gray" variant="soft" ml="1">{detectedTokens.length}</Badge>
              )}
            </Tabs.Trigger>
            <Tabs.Trigger value="attachments"><Paperclip size={14} /> Attachments</Tabs.Trigger>
          </Tabs.List>

          <Tabs.Content value="edit">
            <Box mt="3">
              <EmailTemplateToolbar
                content={editData.content}
                onInsert={insertIntoActive}
                workflowContext={workflowContext}
              />
              <Box style={{ height: editorHeight, overflow: "auto" }}>
                <RichTextEditor
                  value={editData.content}
                  onChange={(v) => handleFieldChange("content", v)}
                  enableMarkdown={false}
                  placeholder="Write your email content here…"
                  onEditorReady={(editor) => { tipTapEditorRef.current = editor; }}
                />
              </Box>
            </Box>
          </Tabs.Content>

          <Tabs.Content value="source">
            <Box mt="3">
              <Flex align="center" justify="between" gap="2" mb="2">
                <EmailTemplateToolbar
                  content={editData.content}
                  onInsert={insertIntoActive}
                  workflowContext={workflowContext}
                />
                <Button
                  type="button"
                  variant="soft"
                  size="1"
                  color="gray"
                  onClick={() => handleFieldChange("content", formatHtml(editData.content))}
                  title="Pretty-print the HTML"
                >
                  Format
                </Button>
              </Flex>
              <Box style={{
                height: editorHeight,
                border: "1px solid var(--gray-6)",
                borderRadius: "var(--radius-2)",
                overflow: "hidden",
              }}>
                <CodeMirror
                  value={editData.content}
                  extensions={[cmHtml(), EditorView.lineWrapping]}
                  onChange={(v) => handleFieldChange("content", v)}
                  onCreateEditor={(view) => { editorViewRef.current = view; }}
                  basicSetup={{ lineNumbers: true, foldGutter: true, highlightActiveLine: true }}
                  style={{ fontSize: 13, height: "100%" }}
                />
              </Box>
            </Box>
          </Tabs.Content>

          <Tabs.Content value="preview">
            <Box mt="3">
              <Text size="1" color="gray" mb="2" style={{ display: "block" }}>
                Preview renders the template with the current mappings. Step
                expressions resolve at run time.
              </Text>
              {previewHtml ? (
                <iframe
                  srcDoc={previewHtml}
                  sandbox=""
                  style={{
                    width: "100%",
                    height: previewHeight,
                    border: "1px solid var(--gray-6)",
                    borderRadius: "var(--radius-2)",
                    background: "white",
                  }}
                  title="Email preview"
                />
              ) : (
                <Text size="2" color="gray">Loading preview…</Text>
              )}
            </Box>
          </Tabs.Content>

          <Tabs.Content value="mappings">
            <Box mt="3">
              <MappingsReview
                detectedTokens={detectedTokens}
                payloadJson={payloadJson}
                setPayload={(next) => setFieldValue("parameters.payload", next)}
                sampleData={sampleData}
                setSampleData={setSampleData}
                existingSteps={existingSteps}
                triggerEntity={triggerEntity}
              />
            </Box>
          </Tabs.Content>

          <Tabs.Content value="attachments">
            <Box mt="3">
              <EmailAttachmentsField
                existingSteps={existingSteps}
                triggerEntity={triggerEntity}
              />
            </Box>
          </Tabs.Content>
        </Tabs.Root>
      </Box>
    </Card>
  );

  const headerBar = (
    <Box mb="3">
      <Text size="2" weight="medium" mb="1" style={{ display: "block" }}>
        Email template
      </Text>
      <Flex align="center" gap="2" wrap="wrap">
        <Box style={{ flex: 1, minWidth: 220 }}>
          <Select.Root
            value={templateType ?? ""}
            onValueChange={(v) => setFieldValue("parameters.template_type", v)}
          >
            <Select.Trigger placeholder="Select a template…" style={{ width: "100%" }} />
            <Select.Content position="popper" style={{ zIndex: 11000 }}>
              {templates?.filter((t) => !t.is_system).map((t) => (
                <Select.Item key={t.template_type} value={t.template_type}>
                  {t.display_name || t.template_type}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Box>
        <Button type="button" variant="soft" size="2" onClick={() => setCreateOpen(true)} disabled={isFreePlan}>
          <Plus size={14} /> New template
        </Button>
        {savedAt && (
          <Flex align="center" gap="1" style={{ color: "var(--green-11)" }}>
            <Check size={14} />
            <Text size="2">Saved</Text>
          </Flex>
        )}
        <Button
          type="button"
          size="2"
          onClick={() => updateMutation.mutate()}
          disabled={isFreePlan || !hasChanges || updateMutation.isPending || !selectedTemplate}
        >
          <Save size={14} />
          {updateMutation.isPending ? "Saving…" : "Save template"}
        </Button>
        <IconButton
          type="button"
          variant="soft"
          color="gray"
          size="2"
          onClick={() => setPopOut((v) => !v)}
          title={popOut ? "Pop back inline" : "Pop out"}
        >
          {popOut ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
        </IconButton>
        {popOut && (
          <IconButton type="button" variant="ghost" color="gray" size="2" onClick={() => setPopOut(false)} title="Close">
            <X size={14} />
          </IconButton>
        )}
      </Flex>
    </Box>
  );

  const fullBody = (
    <Box>
      {isFreePlan && (
        <Callout.Root color="amber" mb="3">
          <Callout.Icon>
            <Lock size={16} />
          </Callout.Icon>
          <Callout.Text>
            Sending emails from workflows is not available on the free plan, so this step will fail when it runs.
            Upgrade your Anythink plan to send emails and edit templates.
          </Callout.Text>
        </Callout.Root>
      )}
      <Box mb="3">
        <FieldSectionHeader
          icon={<Users size={14} />}
          title="Audience"
          subtitle="Who receives this email. Use a workflow variable to resolve to the recipient's address at run time."
        />
      </Box>
      <FromAddressField />
      <VariableInputField
        name="parameters.to"
        label="To Email Address"
        placeholder="recipient@example.com or {{$anythink.trigger.data.email}}"
        required
        existingSteps={existingSteps}
        triggerEntity={triggerEntity}
      />
      <Box mb="3">
        <Text size="2" color="gray">
          Use <Code size="1">{`{{ }}`}</Code> syntax to reference dynamic values
          (e.g., <Code size="1">{`{{$anythink.trigger.data.email}}`}</Code>)
        </Text>
      </Box>
      {headerBar}
      {editorPanel}

      <CreateTemplateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(t) => {
          setFieldValue("parameters.template_type", t.template_type);
          setCreateOpen(false);
        }}
      />
    </Box>
  );

  if (popOut) {
    return (
      <Box
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.7)",
          zIndex: 20000,
          display: "flex",
          alignItems: "stretch",
          justifyContent: "center",
          padding: 24,
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) setPopOut(false);
        }}
      >
        <Box
          style={{
            background: "var(--color-panel-solid)",
            borderRadius: 8,
            boxShadow: "var(--shadow-6)",
            width: "min(1400px, 100%)",
            height: "calc(100vh - 48px)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <PopOutSaveBar onClose={() => setPopOut(false)} />
          <Box style={{ flex: 1, overflow: "auto", padding: 20 }}>
            {fullBody}
          </Box>
        </Box>
      </Box>
    );
  }

  return fullBody;
}

interface MappingsReviewProps {
  detectedTokens: string[];
  payloadJson: string;
  setPayload: (next: string) => void;
  sampleData: Record<string, string>;
  setSampleData: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  existingSteps: WorkflowStep[];
  triggerEntity?: string;
}

function MappingsReview({
  detectedTokens,
  payloadJson,
  setPayload,
  sampleData,
  setSampleData,
  existingSteps,
  triggerEntity,
}: MappingsReviewProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const payloadMap = useMemo(() => {
    const out: Record<string, string> = {};
    try {
      const parsed = JSON.parse(payloadJson || "{}");
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        for (const [k, v] of Object.entries(parsed)) {
          out[k] = typeof v === "string" ? v : JSON.stringify(v);
        }
      }
    } catch { /* ignore */ }
    return out;
  }, [payloadJson]);

  const removeKey = (key: string) => {
    const next = { ...payloadMap };
    delete next[key];
    setPayload(JSON.stringify(next, null, 2));
  };
  const updateKey = (key: string, value: string) => {
    const next = { ...payloadMap, [key]: value };
    setPayload(JSON.stringify(next, null, 2));
  };

  const mappedKeys = Object.keys(payloadMap);
  const unmappedTokens = detectedTokens.filter((t) => !(t in payloadMap));
  const extraKeys = mappedKeys.filter((k) => !detectedTokens.includes(k));

  const setSample = (token: string, value: string) =>
    setSampleData((prev) => ({ ...prev, [token]: value }));

  return (
    <Box>
      <Flex align="center" justify="between" mb="2">
        <Box>
          <Text size="2" weight="medium">Mappings</Text>
          <Text size="1" color="gray" style={{ display: "block" }}>
            Tokens populate as you use <Code size="1">Insert variable → From this workflow</Code>. Add a sample value to see the preview render with real-looking data.
          </Text>
        </Box>
        <PayloadHelpDialog />
      </Flex>

      {mappedKeys.length === 0 && unmappedTokens.length === 0 ? (
        <Text size="2" color="gray">
          This template only uses built-in tokens (<Code size="1">{`{{OrgName}}`}</Code>,{" "}
          <Code size="1">{`{{BrandColor}}`}</Code>, etc.). Nothing to map.
        </Text>
      ) : (
        <Flex direction="column" gap="2">
          {mappedKeys.map((token) => (
            <MappingRow
              key={token}
              token={token}
              expression={payloadMap[token]}
              sample={sampleData[token] ?? ""}
              onChange={(v) => updateKey(token, v)}
              onSampleChange={(v) => setSample(token, v)}
              onRemove={() => removeKey(token)}
              extra={!detectedTokens.includes(token)}
              existingSteps={existingSteps}
              triggerEntity={triggerEntity}
            />
          ))}
          {unmappedTokens.map((token) => (
            <UnmappedRow
              key={token}
              token={token}
              sample={sampleData[token] ?? ""}
              onChange={(v) => updateKey(token, v)}
              onSampleChange={(v) => setSample(token, v)}
              existingSteps={existingSteps}
              triggerEntity={triggerEntity}
            />
          ))}
        </Flex>
      )}

      {extraKeys.length > 0 && (
        <Text size="1" color="amber" mt="2" style={{ display: "block" }}>
          {extraKeys.length} mapping{extraKeys.length === 1 ? "" : "s"} not referenced by the template — they'll still be sent.
        </Text>
      )}

      <Box mt="3">
        <Button
          type="button"
          variant="ghost"
          size="1"
          color="gray"
          onClick={() => setShowAdvanced((v) => !v)}
        >
          {showAdvanced ? "Hide raw JSON" : "Edit raw JSON"}
        </Button>
        {showAdvanced && (
          <Box mt="2">
            <CodeField
              name="parameters.payload"
              language="application/json"
              isEditing={true}
              typeDefinitions={ANYTHINK_TYPE_DEFINITIONS}
              disableValidation={true}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

const MAPPING_GRID = "140px 16px 1fr 64px";

function MappingRow({
  token, expression, sample, onChange, onSampleChange, onRemove, extra, existingSteps, triggerEntity,
}: {
  token: string;
  expression: string;
  sample: string;
  onChange: (next: string) => void;
  onSampleChange: (next: string) => void;
  onRemove: () => void;
  extra: boolean;
  existingSteps: WorkflowStep[];
  triggerEntity?: string;
}) {
  return (
    <Box style={{
      padding: "10px 12px",
      border: "1px solid var(--gray-5)",
      borderRadius: 6,
      background: extra ? "var(--amber-2)" : "transparent",
      display: "grid",
      gridTemplateColumns: MAPPING_GRID,
      columnGap: 8,
      rowGap: 8,
      alignItems: "center",
    }}>
      <Code size="1" style={{ fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{`{{${token}}}`}</Code>
      <Text size="1" color="gray" style={{ textAlign: "center" }}>→</Text>
      <TextField.Root
        size="2"
        value={expression}
        onChange={(e) => onChange(e.target.value)}
        style={{ fontFamily: expression.includes("{{") ? "monospace" : undefined }}
      />
      <Flex gap="1" justify="end">
        <VariableMenu
          existingSteps={existingSteps}
          triggerEntity={triggerEntity}
          onInsert={(text) => onChange(text)}
        />
        <IconButton type="button" variant="ghost" color="gray" size="2" onClick={onRemove} title="Remove mapping">
          <X size={14} />
        </IconButton>
      </Flex>

      <Text size="1" color="gray">Sample for preview</Text>
      <Text size="1" color="gray" style={{ textAlign: "center" }} aria-hidden>·</Text>
      <TextField.Root
        size="2"
        value={sample}
        onChange={(e) => onSampleChange(e.target.value)}
        placeholder={`e.g. ${exampleFor(token)}`}
      />
      <Box />
    </Box>
  );
}

function UnmappedRow({
  token, sample, onChange, onSampleChange, existingSteps, triggerEntity,
}: {
  token: string;
  sample: string;
  onChange: (next: string) => void;
  onSampleChange: (next: string) => void;
  existingSteps: WorkflowStep[];
  triggerEntity?: string;
}) {
  const [draft, setDraft] = useState("");
  const commit = (next: string) => {
    setDraft(next);
    if (next) onChange(next);
  };
  return (
    <Box style={{
      padding: "10px 12px",
      border: "1px dashed var(--red-7)",
      borderRadius: 6,
      background: "var(--red-2)",
      display: "grid",
      gridTemplateColumns: MAPPING_GRID,
      columnGap: 8,
      rowGap: 8,
      alignItems: "center",
    }}>
      <Flex align="center" gap="1" style={{ minWidth: 0 }}>
        <Code size="1" style={{ fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{`{{${token}}}`}</Code>
      </Flex>
      <Text size="1" color="red" style={{ textAlign: "center" }}>→</Text>
      <TextField.Root
        size="2"
        value={draft}
        placeholder="Pick a variable or type a literal value…"
        onChange={(e) => commit(e.target.value)}
        style={{ fontFamily: draft.includes("{{") ? "monospace" : undefined }}
      />
      <Flex justify="end">
        <VariableMenu
          existingSteps={existingSteps}
          triggerEntity={triggerEntity}
          onInsert={(text) => commit(text)}
        />
      </Flex>

      <Text size="1" color="gray">Sample for preview</Text>
      <Text size="1" color="gray" style={{ textAlign: "center" }} aria-hidden>·</Text>
      <TextField.Root
        size="2"
        value={sample}
        onChange={(e) => onSampleChange(e.target.value)}
        placeholder={`e.g. ${exampleFor(token)}`}
      />
      <Box />
    </Box>
  );
}

function FromAddressField() {
  const { values, setFieldValue } = useFormikContext<any>();
  const current = (values?.parameters?.from as string | undefined) ?? "";

  const { data } = useQuery({
    queryKey: ["tenant-domains"],
    queryFn: () => tenantDomainService.list(),
  });

  const verifiedHosts = useMemo(
    () => (data?.domains || []).filter((d) => d.is_verified).map((d) => d.hostname),
    [data?.domains],
  );
  const defaultSender = data?.sender_addresses?.find((s) => s.is_default)?.address ?? null;

  const { local, host } = useMemo(() => {
    const parts = splitFromAddress(current);
    if (!parts.host) parts.host = verifiedHosts[0] ?? "";
    return parts;
  }, [current, verifiedHosts]);

  const setFrom = (nextLocal: string, nextHost: string) => {
    setFieldValue("parameters.from", composeFromAddress(nextLocal, nextHost));
  };

  return (
    <Box mb="3">
      <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>
        From address
      </Text>
      {verifiedHosts.length === 0 ? (
        <Box style={{
          padding: "8px 10px",
          border: "1px dashed var(--amber-7)",
          borderRadius: 6,
          background: "var(--amber-2)",
        }}>
          <Text size="2">
            No verified sending domains. This step will fail to send until a
            domain is verified.{" "}
            <a href="/settings/email-templates?tab=sending-domains" style={{ color: "var(--accent-11)" }}>
              Add a sending domain →
            </a>
          </Text>
        </Box>
      ) : (
        <>
          <Flex gap="2" align="center">
            <TextField.Root
              size="2"
              placeholder="noreply"
              value={local}
              onChange={(e) => setFrom(e.target.value, host || verifiedHosts[0])}
              style={{ flex: 1 }}
            />
            <Text size="2" color="gray">@</Text>
            <Select.Root
              value={host && verifiedHosts.includes(host) ? host : verifiedHosts[0]}
              onValueChange={(v) => setFrom(local || "noreply", v)}
            >
              <Select.Trigger style={{ minWidth: 200 }} />
              <Select.Content position="popper" style={{ zIndex: 11000 }}>
                {verifiedHosts.map((h) => (
                  <Select.Item key={h} value={h}>{h}</Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
            <Button
              type="button"
              variant="soft"
              color="gray"
              size="2"
              onClick={() => setFieldValue("parameters.from", "")}
              title="Use tenant default sender"
            >
              Use default
            </Button>
          </Flex>
          <Text size="1" color="gray" mt="1" style={{ display: "block" }}>
            {current
              ? `Sending as ${current}`
              : defaultSender
                ? `Will use tenant default: ${defaultSender}`
                : "No tenant default set — configure one in Settings → Email Templates → Sending Domains."}
          </Text>
        </>
      )}
    </Box>
  );
}

function appendWithSpacing(existing: string, addition: string): string {
  if (!existing) return addition;
  if (existing.endsWith("\n") || existing.endsWith(">")) return existing + addition;
  return existing + "\n" + addition;
}

function buildPreviewVariables(
  json: string,
  sample: Record<string, string>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  try {
    const parsed = JSON.parse(json || "{}");
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      for (const [k, v] of Object.entries(parsed)) {
        if (sample[k] !== undefined && sample[k] !== "") {
          out[k] = sample[k];
          continue;
        }
        if (typeof v === "string" && v.includes("{{")) {
          out[k] = `[${k}]`;
        } else {
          out[k] = v;
        }
      }
    }
  } catch { /* ignore */ }
  for (const [k, v] of Object.entries(sample)) {
    if (!(k in out) && v !== "") out[k] = v;
  }
  return out;
}

function CreateTemplateDialog({
  open, onOpenChange, onCreated,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onCreated: (t: EmailTemplate) => void;
}) {
  const queryClient = useQueryClient();
  const [data, setData] = useState({ template_type: "", subject: "" });

  const mutation = useMutation({
    mutationFn: () =>
      emailTemplateService.createTemplate({
        ...data,
        content: "<p>Enter your email content here...</p>",
      }),
    onSuccess: (created) => {
      queryClient.setQueryData<EmailTemplate[] | undefined>(
        ["email-templates"],
        (prev) => {
          const others = (prev ?? []).filter((t) => t.template_type !== created.template_type);
          return [...others, created];
        },
      );
      queryClient.invalidateQueries({ queryKey: ["email-templates"] });
      setData({ template_type: "", subject: "" });
      onCreated(created);
    },
  });

  return (
    <HighZIndexDialog
      open={open}
      onOpenChange={onOpenChange}
      title="New email template"
      description="Templates are project-scoped and editable from this dialog or the Email Templates settings page."
      maxWidth={480}
    >
      <Card mb="3">
        <Flex direction="column" gap="3" p="3">
          <Box>
            <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>
              Template type (slug)
            </Text>
            <TextField.Root
              size="2"
              value={data.template_type}
              onChange={(e) => setData((d) => ({ ...d, template_type: e.target.value }))}
              placeholder="welcome_email"
            />
          </Box>
          <Box>
            <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>
              Subject
            </Text>
            <TextField.Root
              size="2"
              value={data.subject}
              onChange={(e) => setData((d) => ({ ...d, subject: e.target.value }))}
              placeholder="Welcome to {{OrgName}}"
            />
          </Box>
        </Flex>
      </Card>
      <Flex justify="end" gap="2">
        <Dialog.Close asChild>
          <Button type="button" variant="soft" color="gray">Cancel</Button>
        </Dialog.Close>
        <Button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={!data.template_type || !data.subject || mutation.isPending}
        >
          {mutation.isPending ? "Creating…" : "Create template"}
        </Button>
      </Flex>
    </HighZIndexDialog>
  );
}
